import React, { useState } from "react";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { sendOtpApi, verifyOtpApi, checkUserExists } from "../../utils/api"; 
import '../../css/phoneauth.css';

const Icons = {
  Back: () => <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12"></line><polyline points="12 19 5 12 12 5"></polyline></svg>,
  Eye: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path><circle cx="12" cy="12" r="3"></circle></svg>,
  EyeOff: () => <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path><line x1="1" y1="1" x2="23" y2="23"></line></svg>,
  Cam: () => <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path><circle cx="12" cy="13" r="4"></circle></svg>
};

export default function SignupForm({ handleAuthSuccess }) {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [image, setImage] = useState(null);
  const [preview, setPreview] = useState(null);
  
  const [localError, setLocalError] = useState("");
  
  const [form, setForm] = useState({ 
    firstName: '', lastName: '', dob: '', email: '', password: '', phone: '', otp: '' 
  });

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setLocalError(""); 
  };

  const handleImg = (e) => {
    if (e.target.files[0]) {
      setImage(e.target.files[0]);
      setPreview(URL.createObjectURL(e.target.files[0]));
      setLocalError("");
    }
  };

  const pwdStrength = {
    length: form.password.length >= 8,
    upper: /[A-Z]/.test(form.password),
    number: /[0-9]/.test(form.password),
    special: /[@$!%*?&#]/.test(form.password)
  };
  const isPasswordStrong = Object.values(pwdStrength).every(Boolean);

  const goNext = (e) => { e.preventDefault(); setStep(step + 1); setLocalError(""); };
  const goBack = () => { if (step > 1) { setStep(step - 1); setLocalError(""); } };

  const handleStep1 = (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName) return setLocalError("Please enter your full name.");
    setLocalError("");
    setStep(2);
  };

  const handleStep2 = (e) => {
    e.preventDefault();
    if (!form.dob) return setLocalError("Please enter your Date of Birth.");
    setLocalError("");
    setStep(3);
  };

  const handleStep3 = async (e) => {
    e.preventDefault();
    if (!form.email || !form.password) return setLocalError("Please complete all fields.");
    
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(form.email)) {
      return setLocalError("Please enter a valid email address (e.g., name@example.com).");
    }

    if (!isPasswordStrong) return setLocalError("Please make your password stronger.");
    
    setLoading(true);
    setLocalError("");
    
    try {
      await checkUserExists(form.email, ""); 
      setStep(4);
    } catch (err) {
      setLocalError(err.message || "Invalid email address.");
    } finally {
      setLoading(false);
    }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();
    if (!form.phone || form.phone.length < 10) return setLocalError("Please enter a valid mobile number.");
    setLoading(true);
    setLocalError("");
    
    try {
      const fullPhone = form.phone.startsWith('+') ? form.phone : `+91${form.phone}`;
      await checkUserExists(form.email, fullPhone); 
      await sendOtpApi(fullPhone);
      setStep(6); 
    } catch (err) {
      const errMsg = err.response?.data?.message || err.response?.data?.error || err.message || "";
      const lowerMsg = errMsg.toLowerCase();

      if (lowerMsg.includes("email") && (lowerMsg.includes("already") || lowerMsg.includes("registered"))) {
        setLocalError("This email is already registered. Please log in or use another email.");
        setStep(3); 
      } 
      else if (lowerMsg.includes("phone") && (lowerMsg.includes("already") || lowerMsg.includes("registered"))) {
        setLocalError("This phone number is already registered. Please use another number.");
      } 
      else if (lowerMsg.includes("invalid")) {
        setLocalError("This phone number format is invalid. Please check and try again.");
      } 
      // 🚀 UPGRADE: Removed Twilio trap. Now dynamically displays exact backend/Fast2SMS errors.
      else {
        setLocalError(`WhatsApp API Error: ${errMsg || "Could not send code. Please try again."}`);
      }
    } finally { 
      setLoading(false); 
    }
  };

  const handleFinalSignup = async (e) => {
    e.preventDefault();
    if (form.otp.length !== 6) return setLocalError("Enter the 6-digit OTP.");
    setLoading(true);
    setLocalError("");
    
    try {
      const formData = new FormData();
      formData.append("name", `${form.firstName} ${form.lastName}`);
      formData.append("email", form.email);
      formData.append("password", form.password);
      formData.append("dob", form.dob);
      formData.append("phone", form.phone.startsWith('+') ? form.phone : `+91${form.phone}`);
      formData.append("code", form.otp);
      if (image) formData.append("image", image);

      const data = await verifyOtpApi(formData);
      handleAuthSuccess(data);
    } catch (err) {
      setLocalError(err.response?.data?.message || "Invalid OTP.");
    } finally { setLoading(false); }
  };

  return (
    <div className="flow-form-container">
      
      <div className="flow-header">
        <button type="button" className={`flow-back-btn ${step === 1 ? 'hidden' : ''}`} onClick={goBack}>
          <Icons.Back />
        </button>
        <div className="flow-progress-track">
          <div className="flow-progress-fill" style={{ width: `${(step / 6) * 100}%` }}></div>
        </div>
      </div>

      <div className="flow-slides-wrapper">
        
        {localError && (
          <div style={{
            backgroundColor: "#FCE8E6",
            color: "#D93025",
            padding: "10px 14px",
            borderRadius: "8px",
            fontSize: "14px",
            marginBottom: "16px",
            border: "1px solid #FAD2CF",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            width: "100%",
            boxSizing: "border-box"
          }}>
            <span>⚠️</span> {localError}
          </div>
        )}

        {/* STEP 1: NAME */}
        {step === 1 && (
          <form onSubmit={handleStep1} className="flow-slide fade-in-right">
            <h2 className="flow-title">What's your name?</h2>
            <div className="flow-input-group">
              <input name="firstName" placeholder="First Name" value={form.firstName} onChange={handleChange} autoFocus required />
            </div>
            <div className="flow-input-group">
              <input name="lastName" placeholder="Last Name" value={form.lastName} onChange={handleChange} required />
            </div>
            <button type="submit" className="flow-primary-btn">Continue</button>
          </form>
        )}

        {/* STEP 2: DOB */}
        {step === 2 && (
          <form onSubmit={handleStep2} className="flow-slide fade-in-right">
            <h2 className="flow-title">When is your birthday?</h2>
            <p className="flow-subtitle">You must be at least 18 years old to use Vyango.</p>
            <div className="flow-input-group">
              <input name="dob" type="date" value={form.dob} onChange={handleChange} autoFocus required />
            </div>
            <button type="submit" className="flow-primary-btn">Continue</button>
          </form>
        )}

        {/* STEP 3: CREDENTIALS */}
        {step === 3 && (
          <form onSubmit={handleStep3} className="flow-slide fade-in-right">
            <h2 className="flow-title">Account details</h2>
            <div className="flow-input-group">
              <input 
                name="email" 
                type="email" 
                placeholder="Email address" 
                value={form.email} 
                onChange={handleChange} 
                autoFocus 
                required 
                autoCapitalize="none" 
                autoCorrect="off" 
              />
            </div>
            
            <div className="flow-input-group password-group">
              <input name="password" type={showPass ? "text" : "password"} placeholder="Create a password" value={form.password} onChange={handleChange} required />
              <button type="button" className="flow-eye-btn" onClick={() => setShowPass(!showPass)}>
                {showPass ? <Icons.EyeOff /> : <Icons.Eye />}
              </button>
            </div>

            {form.password.length > 0 && (
              <div className="flow-pwd-meter">
                <div className={`pwd-bar ${pwdStrength.length ? 'fill' : ''}`}></div>
                <div className={`pwd-bar ${pwdStrength.upper ? 'fill' : ''}`}></div>
                <div className={`pwd-bar ${pwdStrength.number ? 'fill' : ''}`}></div>
                <div className={`pwd-bar ${pwdStrength.special ? 'fill' : ''}`}></div>
              </div>
            )}
            {form.password.length > 0 && !isPasswordStrong && (
              <p className="flow-pwd-hint">Use 8+ chars, an uppercase letter, a number, and a symbol.</p>
            )}

            <button type="submit" className="flow-primary-btn" disabled={form.password.length > 0 && !isPasswordStrong}>
              Continue
            </button>
          </form>
        )}

        {/* STEP 4: PROFILE PHOTO */}
        {step === 4 && (
          <form onSubmit={goNext} className="flow-slide fade-in-right text-center">
            <h2 className="flow-title">Add a profile photo</h2>
            <p className="flow-subtitle">Drivers and passengers like to see who they are traveling with.</p>
            
            <div className="flow-photo-upload">
              <input type="file" id="flow-pfp" hidden accept="image/*" onChange={handleImg} />
              <label htmlFor="flow-pfp" className="flow-pfp-label">
                {preview ? <img src={preview} alt="Profile" /> : <div className="flow-cam"><Icons.Cam /></div>}
                <div className="flow-pfp-add">+</div>
              </label>
            </div>

            <div className="flow-btn-stack">
              <button type="submit" className="flow-primary-btn">{preview ? "Save & Continue" : "Add Photo"}</button>
              <button type="button" className="flow-text-btn" onClick={() => setStep(5)}>Skip for now</button>
            </div>
          </form>
        )}

        {/* STEP 5: PHONE NUMBER */}
        {step === 5 && (
          <form onSubmit={handleSendOtp} className="flow-slide fade-in-right">
            <h2 className="flow-title">Verify your number</h2>
            <p className="flow-subtitle">We'll send a code to verify your identity on WhatsApp.</p>
            
            <div className="flow-phone-wrapper">
              <PhoneInput 
                value={form.phone} 
                onChange={(val) => {
                  setForm({...form, phone: val});
                  setLocalError("");
                }} 
                defaultCountry="IN" 
                placeholder="Mobile Number" 
                required 
              />
            </div>

            <button type="submit" className="flow-primary-btn" disabled={loading}>
              {loading ? <span className="flow-loader"></span> : "Send verification code"}
            </button>
          </form>
        )}

        {/* STEP 6: OTP */}
        {step === 6 && (
          <form onSubmit={handleFinalSignup} className="flow-slide fade-in-right text-center">
            <h2 className="flow-title">Enter code</h2>
            <p className="flow-subtitle">Sent to <b>{form.phone}</b></p>
            
            <input 
              name="otp" 
              className="flow-otp-input" 
              placeholder="000000" 
              value={form.otp} 
              onChange={(e) => {
                setForm({...form, otp: e.target.value.replace(/\D/g, '')});
                setLocalError("");
              }} 
              maxLength={6} 
              autoFocus 
              required 
            />

            <button type="submit" className="flow-primary-btn" disabled={loading}>
              {loading ? <span className="flow-loader"></span> : "Create Account"}
            </button>
            
            <button 
              type="button" 
              onClick={() => { setStep(5); setLocalError(""); }}
              style={{
                marginTop: "16px",
                background: "none",
                border: "none",
                color: "#1A73E8",
                fontSize: "14px",
                fontWeight: "500",
                cursor: "pointer",
                textDecoration: "underline"
              }}
            >
              Change phone number
            </button>
          </form>
        )}

      </div>
    </div>
  );
}